import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { PudoDisplay } from "@narvar/nth-hook-loma";
import {
  PudoOption,
  PudoOptionInput,
  ReturnCart,
  SetPudoOptionMutation,
  SetPudoOptionMutationVariables,
  SetPudoOptionDocument,
  HipaaSetPudoOptionMutation,
  HipaaSetPudoOptionMutationVariables,
  HipaaSetPudoOptionDocument,
} from "@narvar/nth-kit-returns-headless";

import { generatePudoId } from "../transformers/pudoMethod";
import { ExtendedPudoOption } from "@narvar/nth-kit-returns-headless";

export const isDisabled = (
  selectedCategory?: PudoOptionInput | null,
  pudoReference?: PudoOption,
  isCategoryConfirmed?: boolean,
  multiLabelsEnabled?: boolean,
  labelsCount?: number,
): boolean => {
  const hasLocations =
    pudoReference?.dropOff?.sortedLocations?.locationsList &&
    pudoReference?.dropOff?.sortedLocations?.locationsList.length > 0;
  const hasPickup = pudoReference?.pickup;

  const noCategorySelected = !selectedCategory;
  const hasLocationsAndNoLocationSelected =
    hasLocations &&
    !selectedCategory?.selectedDropOff?.selectedLocation &&
    !isCategoryConfirmed;
  const hasPickupAndNoTimeSlotSelected =
    hasPickup && !selectedCategory?.selectedPickup?.selectedTimeSlot;
  const noLabelCount =
    isCategoryConfirmed && multiLabelsEnabled && !labelsCount;

  return [
    noCategorySelected,
    hasLocationsAndNoLocationSelected,
    hasPickupAndNoTimeSlotSelected,
    noLabelCount,
  ].some(Boolean);
};

export const setPudoOption = async ({
  client,
  isHipaaCompliant = false,
  selectedPudoOption,
  returnCart,
}: {
  client?: ApolloClient<NormalizedCacheObject>;
  isHipaaCompliant?: boolean;
  returnCart?: ReturnCart;
  selectedPudoOption?: PudoOptionInput;
}) => {
  if (!client) throw new Error("Client not initialized");
  if (!returnCart?.id || !selectedPudoOption)
    throw new Error("Unable to set PUDO option: Missing required parameters");

  let mutation;

  if (isHipaaCompliant) {
    mutation = await client.mutate<
      HipaaSetPudoOptionMutation,
      HipaaSetPudoOptionMutationVariables
    >({
      mutation: HipaaSetPudoOptionDocument,
      variables: {
        returnCartId: returnCart.id,
        pudoOption: selectedPudoOption,
      },
    });
  } else {
    mutation = await client.mutate<
      SetPudoOptionMutation,
      SetPudoOptionMutationVariables
    >({
      mutation: SetPudoOptionDocument,
      variables: {
        returnCartId: returnCart.id,
        pudoOption: selectedPudoOption,
      },
    });
  }

  const { data } = mutation;
  if (!data)
    throw new Error("An error has occurred while setting the PUDO option");
  return data;
};

// Returns a sorted array of pudoOptions that have been augmented with a sort key.
export const sortPudoOptions = (
  pudoOptions?: ExtendedPudoOption[],
  sortOrder?: PudoDisplay["pudoOptionSort"],
) => {
  if (!pudoOptions || !Array.isArray(sortOrder) || sortOrder.length === 0) {
    return pudoOptions;
  }

  return pudoOptions
    .map((option) => {
      const id = generatePudoId(option);

      const sort = sortOrder.indexOf(id) >= 0 ? sortOrder.indexOf(id) : 100;

      return {
        ...option,
        sort,
      };
    })
    .sort((a, b) => a.sort - b.sort);
};
